import { useFlag, useNewCode } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { SettingsIcon } from '@calendly/ui/components/icons';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { dismissExportConfirmation } from 'actions/scheduled_events/export';
import { useGetUserQuery } from 'common';
import ExportConfirmation from 'components/export/confirmation';
import ScopeDropdown from 'components/home_bar/scope_dropdown';
import { MeetingsFilter } from 'components/meetings';
import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell';
import { PageFilterRenderer } from 'features/app_shell/shell_layout/pageFilterContext';
import { isMIFeatureAvailableSelector } from 'features/billing';
import { useGetMeetingIntelligenceUserSettingsQuery, useNotetakerEntitled, StepTwo, MI_MEETINGS_TOUR, handleCheckStepEnabled, NotetakerSettingsButtonWithSpotlightWrapper } from 'features/meeting_intelligence';
import { SETTINGS_SECTION } from 'features/settings_modal/constants';
import { useSettingsNavigation } from 'features/settings_modal/use_settings_navigation';
import { useFilters } from 'hooks/meetings/filters';
import { isExportConfirmationVisibleSelector } from 'selectors/scheduled_events';
import { NOTETAKER_SETTINGS_PATH } from 'shared/constants/personal_browser_urls';
import { ExportButton } from './export_button';
import { useGetMeetingsFilterCountQuery, useSearchMeetingsSupergraphFilterCountQuery } from './graphql_api';
import { createUseResultsCount } from './hooks/use_results_count';
import { Search as SearchInput } from './meetings_page_search_input';
import { MeetingsPresetTabs } from './meetings_preset_tabs';
import { MeetingsSchedulingSourceSwitch } from './meetings_scheduling_source_switch';
import { PersistentFiltersMenu } from './persistent_filters_menu';
const headerContainerIsPhoneCss = "h1pzthai";
const headerContainerMispotlightCss = "h1ucv90c";
const headerContainerIsPretendingCss = "h2y0gce";
const headerContainerCss = "h49spf8";
const headerContainerLegacyCss = "htz5rl1";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    mispotlight,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  const {
    data
  } = useGetUserQuery();
  const isPretending = data?.is_pretending;
  const isProductRefreshAppShell = useProductRefreshAppShell();
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, {
    // legacy shell only; new shell hoists to top chrome via PageFilterRenderer
    [headerContainerLegacyCss]: !isProductRefreshAppShell,
    [headerContainerIsPhoneCss]: isPhone,
    [headerContainerIsPretendingCss]: isPretending,
    [headerContainerMispotlightCss]: mispotlight
  }, props.className)} />;
});
const searchInputContainerIsTabletCss = "s1mv259i";
const searchInputContainerCss = "s139l2lo";
const SearchInputContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isTablet = useDeviceType('isTablet');
  return <Component ref={ref} {...restProps} className={classNames(searchInputContainerCss, {
    [searchInputContainerIsTabletCss]: isTablet
  }, props.className)} />;
});
const rowIsPhoneCss = "r1ff6x6d";
const rowCss = "rmtqggu";
const mobileScopeRowCss = "m10lcrrd";
const mobileActionsRowCss = "m1vywd35";
const mobileSearchInputCss = "mr4sdwu";
const mobileFilterControlCss = "mu2bgu5";
const schedulingSwitchRowCss = "s1kji16w";
const ctaSectionIsTabletCss = "c150xp99";
const rowIsTabletCss = "rirsjs5";
const Row = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  const isTablet = useDeviceType('isTablet');
  return <Component ref={ref} {...restProps} className={classNames(rowCss, {
    [rowIsPhoneCss]: isPhone,
    [rowIsTabletCss]: isTablet
  }, props.className)} />;
});
const filterSectionCss = "fabhuo4";
const FilterSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(filterSectionCss, props.className)} />;
});
const ctaSectionCss = "c1zanl7";
const ctaButtonLabelCss = "c3cc9ti";
const CtaSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    keepInlineOnTablet = false,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isTablet = useDeviceType('isTablet');
  return <Component ref={ref} {...restProps} className={classNames(ctaSectionCss, {
    // The legacy tablet header lets the CTA section wrap to its own full-width row. In preset
    // mode the only CTA is the compact scheduling toggle, so keep it inline on the right.
    [ctaSectionIsTabletCss]: isTablet && !keepInlineOnTablet
  }, props.className)} />;
});
const MobileHeader = forwardRef(({
  scope,
  canExport,
  useResultsCount,
  isNotetakerEntitled,
  notetakerEnabled,
  filterDefaults,
  initialValue,
  onboardingTourSettingsRef,
  displayedMeetingCount = 0
}, ref) => {
  const location = useLocation();
  const showStepTwo = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_TWO);
  const isFilterSettingsEnabled = useNewCode('sa_filter_settings_mtg_pg', false)?.enabled;
  const isDefaultPresetToggleEnabled = useFlag('sa_default_preset_toggle', false);
  const isPhone = useDeviceType('isPhone');
  const useMobilePresetLayout = isDefaultPresetToggleEnabled && isPhone;
  const showLegacyFilterBarSettings = isFilterSettingsEnabled && !isDefaultPresetToggleEnabled;
  const {
    hrefForSettingsModalEntry
  } = useSettingsNavigation();
  return <HeaderContainer ref={ref} mispotlight={showStepTwo}>
      {!useMobilePresetLayout && <Row>
          <div className={mobileScopeRowCss} data-testid="meetings-mobile-scope-row">
            <ScopeDropdown fitContent />
          </div>
        </Row>}
      <Row className={mobileActionsRowCss}>
        <div className={mobileSearchInputCss} data-testid="meetings-mobile-search-field">
          <SearchInput initialValue={initialValue} filterDefaults={filterDefaults} />
        </div>
        <div className={mobileFilterControlCss}>
          <MeetingsFilter useResultsCount={useResultsCount} filterDefaults={filterDefaults} notetakerEnabled={notetakerEnabled} />
        </div>
        {notetakerEnabled && !isFilterSettingsEnabled && !isDefaultPresetToggleEnabled && <NotetakerSettingsButtonWithSpotlightWrapper as={RouterLink} to={hrefForSettingsModalEntry(SETTINGS_SECTION.ai_notetaker, NOTETAKER_SETTINGS_PATH)} aria-label={I18n.t('publisher.personal_settings.navigation.notetaker_settings')}>
            <SettingsIcon />
          </NotetakerSettingsButtonWithSpotlightWrapper>}
        {!isDefaultPresetToggleEnabled && canExport && <ExportButton text={null} scope={scope} filterDefaults={filterDefaults} />}
        {showLegacyFilterBarSettings && <PersistentFiltersMenu ref={onboardingTourSettingsRef} notetakerEnabled={notetakerEnabled} />}
      </Row>
      <MeetingsPresetTabs displayedMeetingCount={displayedMeetingCount} />
      <StepTwo />
    </HeaderContainer>;
});
const DesktopHeader = forwardRef(({
  scope,
  selectedView,
  canExport,
  useResultsCount,
  clearActiveMeeting,
  notetakerEnabled,
  isNotetakerEntitled,
  filterDefaults,
  initialValue,
  onboardingTourSettingsRef,
  displayedMeetingCount = 0
}, ref) => {
  const location = useLocation();
  const showStepTwo = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_TWO);
  const isFilterSettingsEnabled = useNewCode('sa_filter_settings_mtg_pg', false)?.enabled;
  const isDefaultPresetToggleEnabled = useFlag('sa_default_preset_toggle', false);
  const showLegacyFilterBarSettings = isFilterSettingsEnabled && !isDefaultPresetToggleEnabled;
  const {
    hrefForSettingsModalEntry
  } = useSettingsNavigation();
  return <HeaderContainer ref={ref} mispotlight={showStepTwo} data-pendo-id={!isNotetakerEntitled ? 'new-meetings-page' : undefined}>
      <Row>
        <FilterSection>
          <ScopeDropdown />
          <SearchInputContainer>
            <SearchInput initialValue={initialValue} filterDefaults={filterDefaults} />
          </SearchInputContainer>
          <MeetingsFilter useResultsCount={useResultsCount} filterDefaults={filterDefaults} notetakerEnabled={notetakerEnabled} />
        </FilterSection>
        <CtaSection keepInlineOnTablet={isDefaultPresetToggleEnabled}>
          {notetakerEnabled && !isFilterSettingsEnabled && !isDefaultPresetToggleEnabled && <NotetakerSettingsButtonWithSpotlightWrapper as={RouterLink} to={hrefForSettingsModalEntry(SETTINGS_SECTION.ai_notetaker, NOTETAKER_SETTINGS_PATH)} aria-label={I18n.t('publisher.personal_settings.navigation.notetaker_settings')}>
              <SettingsIcon />
              <span className={ctaButtonLabelCss}>{I18n.t('publisher.personal_settings.navigation.notetaker_settings')}</span>
            </NotetakerSettingsButtonWithSpotlightWrapper>}
          {!isDefaultPresetToggleEnabled && canExport && <ExportButton text={I18n.t('publisher.meetings.export')} scope={scope} filterDefaults={filterDefaults} />}
          {showLegacyFilterBarSettings && <PersistentFiltersMenu ref={onboardingTourSettingsRef} notetakerEnabled={notetakerEnabled} />}
          {isDefaultPresetToggleEnabled && <div className={schedulingSwitchRowCss}>
              <MeetingsSchedulingSourceSwitch />
            </div>}
        </CtaSection>
      </Row>
      <MeetingsPresetTabs displayedMeetingCount={displayedMeetingCount} />
      <StepTwo />
    </HeaderContainer>;
});
export const SearchFilterExport = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const isExportConfirmationVisible = useSelector(isExportConfirmationVisibleSelector);
  const isPhone = useDeviceType('isPhone');
  const isTablet = useDeviceType('isTablet');
  const isProductRefreshAppShell = useProductRefreshAppShell();
  const useResultsCount = createUseResultsCount(useGetMeetingsFilterCountQuery, useSearchMeetingsSupergraphFilterCountQuery);
  const {
    data: meetingIntelligenceUserData
  } = useGetMeetingIntelligenceUserSettingsQuery();
  const isMIBillingFeatureAvailable = useSelector(isMIFeatureAvailableSelector);
  const {
    isEntitled: isNotetakerEntitled
  } = useNotetakerEntitled();
  const notetakerAvailable = !!meetingIntelligenceUserData?.miEnabled || !!isMIBillingFeatureAvailable && isNotetakerEntitled;
  const {
    getFilterParams
  } = useFilters();
  const filterParams = getFilterParams();
  const isDefaultPresetToggleEnabled = useFlag('sa_default_preset_toggle', false);
  const commonProps = {
    ref,
    useResultsCount,
    notetakerEnabled: notetakerAvailable,
    isNotetakerEntitled,
    initialValue: filterParams.search_keyword,
    displayedMeetingCount: props.displayedMeetingCount ?? 0,
    ...props
  };

  // In preset mode, tablet uses the desktop single-row layout (scope + search + filter together,
  // scheduling toggle on the right) per design. Phone keeps the stacked mobile layout, and
  // non-preset tablet keeps the existing compact mobile layout.
  const useMobileLayout = isPhone || isTablet && !isDefaultPresetToggleEnabled;
  const header = useMobileLayout ? <MobileHeader {...commonProps} /> : <DesktopHeader {...commonProps} />;
  const exportConfirmation = isExportConfirmationVisible && <ExportConfirmation onConfirm={() => dispatch(dismissExportConfirmation())} message={I18n.t('publisher.scheduled_events.export.excessive_data')} />;
  if (isProductRefreshAppShell) {
    return <>
        <PageFilterRenderer>{header}</PageFilterRenderer>
        {exportConfirmation}
      </>;
  }
  return <>
      {header}
      {exportConfirmation}
    </>;
});

require("./search_filter_export.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./search_filter_export.js");